/**
 * PEARSON PROPRIETARY AND CONFIDENTIAL INFORMATION SUBJECT TO NDA
 * Copyright © 2025 Pearson Education, Inc. All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Pearson Education, Inc.  The intellectual and technical concepts contained
 * herein are proprietary to Pearson Education, Inc. and may be covered by U.S. and Foreign Patents,
 * patent applications, and are protected by trade secret or copyright law.
 * Dissemination of this information, reproduction of this material, and copying or distribution of this software
 * is strictly forbidden unless prior written permission is obtained
 * from Pearson Education, Inc.
 */

/**
 * @author Gladson Samuel S
 * DashboardCardStyles.js
 * Styles for DashboardCard components
 */

import { makeStyles } from '@material-ui/core';
import mixins from '../../../assets/styles/mixins.scss';

const useDashboardCardStyles = makeStyles({
  root: {
    display: 'flex',
    flexDirection: 'column',
    gap: 16,
    borderRadius: 24,
    '--title-max-width': '100%'
  },
  title: {
    color: mixins.color_text_primary,
    fontFeatureSettings: '\'liga\' off',
    fontFamily: mixins.fontFamilyPlusSans,
    fontSize: mixins.fontSize24,
    fontStyle: 'normal',
    fontWeight: mixins.fontWeight500,
    lineHeight: '32px',
    letterSpacing: '-0.48px',
    padding: '0px 8px',
    cursor: 'initial'
  },
  showCursorPointer: {
    cursor: 'pointer',
    maxWidth: 'var(--title-max-width)'
  },
  iconButton: {
    color: mixins.navyColor,
    fontFeatureSettings: '\'liga\' off',
    fontFamily: mixins.fontFamilyPlusSans,
    fontSize: mixins.fontSize14,
    fontStyle: 'normal',
    fontWeight: mixins.fontWeight500,
    lineHeight: '22px',
    padding: 8,
    borderRadius: '1000px',
    minWidth: 'unset',

    '&:hover': {
      background: mixins.secondaryButtonHoverColor
    },
    '& > span': {
      minWidth: 'unset',
      margin: 0,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '0px 8px 2px 8px'
    },
    '&.Mui-disabled': {
      color: mixins.disabledTextColor,
      opacity: 'unset',
      pointerEvents: 'unset',

      '&:hover': {
        background: mixins.secondaryButtonHoverColor,
        color: 'inherit'
      }
    }
  },
  iconButtonMedium: {
    color: mixins.navyColor,
    maxHeight: 40,
    maxWidth: 40,
    '&:hover': {
      background: mixins.secondaryButtonHoverColor
    },
    '& > span': {
      minWidth: 'unset'
    }
  },
  icon: {
    width: '16px',
    height: '16px'
  },
  iconMedium: {
    width: '44px',
    height: '44px'
  },
  header: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    flexWrap: 'wrap',
    minHeight: 40
  },
  actionsLeftContainer: {
    display: 'flex',
    alignItems: 'flex-start',
    width: '100%',
    flexWrap: 'wrap',
    flexDirection: 'column',
    maxWidth: '90%',
    gap: 8,

    [mixins['md-sm-min']]: {
      width: 'auto',
      flexDirection: 'row'
    }
  },
  actionsLeft: {
    justifyContent: 'flex-end',
    flexGrow: '1',

    [mixins['md-sm-min']]: {
      justifyContent: 'flex-start',
      flexGrow: '0'
    }
  },
  actions: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4
  },
  actionsRight: {
    alignItems: 'flex-start',
    flexDirection: 'column',

    [mixins['md-sm-min']]: {
      flexDirection: 'row'
    }
  },
  content: {},
  iconOnlyButton: {
    '& > span': {
      padding: 0
    }
  },
  circleAroundIcon: {
    padding: 12,
    borderRadius: '50%',
    border: `1px solid ${mixins.secondaryButtonHoverColor}`,
    boxSizing: 'content-box'
  },
  badge: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: `${mixins.badgeBgColoe}`,
    color: `${mixins.accentLightRed}`,
    fontFamily: mixins.fontFamilyPlusSans,
    fontSize: 12,
    padding: '0 4px 2px 4px',
    fontWeight: 600,
    textAlign: 'center',
    lineHeight: '20px',
    marginRight: '-5px'
  },
  subTitle: {
    color: mixins.color_text_primary,
    fontFamily: mixins.fontFamilyPlusSans,
    fontSize: mixins.fontSize16,
    fontWeight: mixins.fontWeight500,
    lineHeight: '24px',
    padding: '0px 8px',
    marginTop: '-8px'
  },
  footer: {
    color: mixins.zambezi,
    fontFamily: mixins.fontFamilyPlusSans,
    fontSize: mixins.fontSize12,
    fontWeight: mixins.fontWeight500,
    lineHeight: '20px',
    display: 'flex',
    alignItems: 'center'
  },
  button: {
    display: 'flex',
    padding: 8,
    justifyContent: 'center',
    alignItems: 'center',
    color: mixins.navyColor,
    fontFamily: 'Plus Jakarta Sans',
    fontSize: 14,
    fontStyle: 'normal',
    fontWeight: '500',
    lineHeight: '22px',
    borderRadius: '1000px',

    '&:hover': {
      background: mixins.secondaryButtonHoverColor
    },
    '& span': {
      padding: '0px 8px'
    }
  }
});

export default useDashboardCardStyles;
